import React, { ReactElement, useContext, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { useCmsAnalytics } from 'b2b-apps/shop/analytics/hooks';
import { ScrollContext } from 'b2b-apps/shop/app/components/App/App/withScrollRestore';
import { useBreadcrumbs } from 'b2b-apps/shop/breadcrumbs/hooks';
import { useComponentsFromModules, useNewsroomComponents } from 'b2b-apps/shop/content/hooks';
import { useEntry } from 'b2b-apps/shop/content/hooks/useEntry';
import { prepareHeadData } from 'b2b-apps/shop/content/utils/headData';
import { resolveModules } from 'b2b-apps/shop/content/utils/modules';
import { generateNewsroomBreadcrumbs, NEWSROOM_MODULES_NAMES } from 'b2b-apps/shop/content/utils/newsroom';
import { useActiveLanguage, useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { getPathnameWithSearch } from 'b2b-apps/shop/routes/utils/url';
import {
  getIsContentTypeDownloadCenter,
  getIsContentTypeNewsroom,
  getIsContentTypeNewsroomCenter,
  getIsContentTypeNewsroomPage,
} from 'b2b-common/core/content/newsroom/utils';
import {
  EnhancedComponentProps,
  EnhancedComponentType,
  HeadDataType,
  InjectedProps,
  MetaTagsType,
  MetaTagType,
  ModulesType,
  WrappedComponentType,
} from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const t = useTranslate();
    const {
      title,
      ...originalProps
    } = props;
    const {
      entryId,
      history,
      location,
    } = originalProps;

    const lang = useActiveLanguage();
    const content: any = useEntry(entryId);
    const getScrollPosition = useContext(ScrollContext);

    const sidebar = useMemo((): ModulesType => {
      const { isLoaded, data } = content;

      if (!isLoaded || !data?.container__sidebar) {
        return [];
      }

      return resolveModules(data.container__sidebar);
    }, [content]);

    const header = useMemo((): ModulesType => {
      const { isLoaded, data } = content;

      if (!isLoaded || !data?.container__top_banner) {
        return [];
      }

      return resolveModules(data.container__top_banner);
    }, [content]);

    const extractHeadData = (
      socialData: Record<string, any> | undefined,
      description: string,
      defaultData: MetaTagType,
    ) => {
      if (description?.length) {
        return prepareHeadData({ ...socialData, meta_description: description }, defaultData);
      }

      return prepareHeadData(socialData, defaultData);
    };

    useCmsAnalytics(content);

    const headData = useMemo((): HeadDataType => {
      const { isLoaded, data } = content;
      const defaultValue: { metaTags: MetaTagsType, title: string } = {
        metaTags: [],
        title: '',
      };
      const rootDataDescription = data?.meta_description;
      const socialMediaHelper = data?.global__helper_socialmedia;

      if (!isLoaded || !data) {
        return defaultValue;
      }

      if (rootDataDescription?.length) {
        const defaultValue = {
          name: 'description',
          content: rootDataDescription,
        };
        if (!socialMediaHelper?.length) {
          return prepareHeadData({}, defaultValue);
        }
        return extractHeadData(socialMediaHelper[0], rootDataDescription, defaultValue);
      }

      if (!rootDataDescription?.length) {
        if (socialMediaHelper?.length) {
          const defaultValue = {
            name: 'description',
            content: t(
              'Shop meta description',
              {},
              'Used as default page description',
            ),
          };
          return extractHeadData(socialMediaHelper[0], '', defaultValue);
        }
      }

      return defaultValue;
    }, [
      content,
      t,
    ]);

    const isNewsroomPage = getIsContentTypeNewsroom(content?.data?.contentTypeId);
    const breadcrumbs = isNewsroomPage ? generateNewsroomBreadcrumbs(content?.data, t('Newsroom'), lang) : [];

    useBreadcrumbs(breadcrumbs);

    const {
      header: newsroomHeaderContent,
      main: newsroomMainContent,
      footer: newsroomFooterContent,
    } = useMemo(() => {
      const { isLoaded, data } = content;
      const components: { header: ModulesType, main: ModulesType, footer: ModulesType } = {
        header: [],
        main: [],
        footer: [],
      };

      if (!isLoaded || !data || !data.contentTypeId) {
        return components;
      }

      const { contentTypeId, loadedContentTypeIds } = data;

      switch (true) {
        case getIsContentTypeNewsroomPage(contentTypeId): {
          components.header = [{
            componentName: NEWSROOM_MODULES_NAMES.newsroomHeader,
            componentData: { ...data, lang },
          }];
          components.footer = [{
            componentName: NEWSROOM_MODULES_NAMES.newsroomFooter,
            componentData: loadedContentTypeIds,
          }];
          break;
        }
        case getIsContentTypeNewsroomCenter(contentTypeId): {
          components.main = [{
            componentName: NEWSROOM_MODULES_NAMES.newsroomCenter,
          }];
          break;
        }
        case getIsContentTypeDownloadCenter(contentTypeId): {
          components.main = [{
            componentName: NEWSROOM_MODULES_NAMES.downloadCenter,
          }];
          break;
        }
      }

      return components;
    },
    [content, lang]);

    useEffect(
      (): void => {
        if (content.isLoaded && history.action === 'POP') {
          const scrollPosition = getScrollPosition(getPathnameWithSearch(location));
          window.scrollTo(0, scrollPosition);
        }
      }, [
        content.isLoaded,
        history.action,
        location,
        getScrollPosition,
      ],
    );

    const pageTitle: string = title || headData.title || content.data?.title || '';
    const headerComponents = useComponentsFromModules(header);
    const sidebarComponents = useComponentsFromModules(sidebar);
    const newsroomHeader = useNewsroomComponents(newsroomHeaderContent);
    const newsroomMain = useNewsroomComponents(newsroomMainContent);
    const newsroomFooter = useNewsroomComponents(newsroomFooterContent);

    const injectedProps: InjectedProps = {
      content,
      headerComponents,
      newsroomHeader,
      newsroomFooter,
      newsroomMain,
      sidebarComponents,
      headData,
      pageTitle,
      entryId,
    };

    return <Component {...injectedProps} {...originalProps} />;
  };

  return compose<EnhancedComponentType>(withRouter)(React.memo(WithContainer));
};

export { withContainer };
